import { listHolidays, type HolidayDto } from '../../../api/calendar';
import { registerSessionCache } from '../../../lib/session-caches';

/**
 * 캘린더 공휴일 — «연도(＋국가)마다 한 번만» 받는다 (2026-10-30).
 *
 * ## 왜
 *
 * 종전에는 공휴일 effect 가 `range.from · range.to` 를 들어, 연속 스크롤이 구간을 넓힐 때마다
 * **같은 연도**의 공휴일을 처음부터 다시 받았다. 6101 실측: `holidays/effective?year=2027` ·
 * `holidays?year=2027` · `calendar/events…` 가 줄줄이 429 — 캘린더 혼자 사용자별 한도를 썼다.
 *
 * ## 규칙
 *
 * - 받은 연도는 세션 동안 기억한다(로그아웃하면 비운다 — `lib/session-caches`).
 * - 같은 연도를 동시에 두 번 부르면 «진행 중 Promise» 를 함께 쓴다 — 요청은 하나다.
 * - 실패한 연도는 기억하지 않는다(다음 기회에 다시). 단 실패 직후 `HOLIDAY_RETRY_MS` 안에는
 *   다시 부르지 않는다 — 구간이 바뀔 때마다 곧바로 다시 부르면 그것이 또 폭주가 된다.
 */
export const HOLIDAY_RETRY_MS = 60_000;

const holidayCache = new Map<string, HolidayDto[]>();
const inflightCache = new Map<string, Promise<HolidayDto[] | null>>();
const failedAtCache = new Map<string, number>();

registerSessionCache(() => {
  holidayCache.clear();
  inflightCache.clear();
  failedAtCache.clear();
});

/** 시험 전용 — 통 사이에 기억이 새지 않게 비운다. */
export function _resetHolidayYearCacheForTests(): void {
  holidayCache.clear();
  inflightCache.clear();
  failedAtCache.clear();
}

const keyOf = (year: number, country: string) => `${country}:${year}`;

/** 이미 받아 둔 그 연도의 공휴일 — 없으면 undefined. */
export function cachedHolidayYear(year: number, country = 'KR'): HolidayDto[] | undefined {
  return holidayCache.get(keyOf(year, country));
}

/**
 * 그 연도의 공휴일을 받는다. 받아 둔 것이면 요청 없이, 진행 중이면 그 Promise 를, 방금 실패했으면
 * 요청 없이 null 을 준다. 실패는 던지지 않고 null 로 끝낸다(표시 레이어 — 오류 문구 없음).
 */
export function loadHolidayYear(year: number, country = 'KR', now: number = Date.now()): Promise<HolidayDto[] | null> {
  const key = keyOf(year, country);
  const hit = holidayCache.get(key);
  if (hit) return Promise.resolve(hit);
  const running = inflightCache.get(key);
  if (running) return running;
  const failedAt = failedAtCache.get(key);
  if (failedAt !== undefined && now - failedAt < HOLIDAY_RETRY_MS) return Promise.resolve(null);

  const p = listHolidays(year, country)
    .then((list) => {
      const clean = (Array.isArray(list) ? list : []).filter(Boolean);
      holidayCache.set(key, clean);
      failedAtCache.delete(key);
      return clean;
    })
    .catch(() => {
      failedAtCache.set(key, Date.now());
      return null;
    })
    .finally(() => { inflightCache.delete(key); });
  inflightCache.set(key, p);
  return p;
}

/** 구간 [from, to] 가 걸친 연도들 — 오름차순. */
export function yearsBetween(fromIso: string, toIso: string): number[] {
  const a = new Date(fromIso).getFullYear();
  const b = new Date(toIso).getFullYear();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return [];
  const out: number[] = [];
  for (let y = Math.min(a, b); y <= Math.max(a, b); y += 1) out.push(y);
  return out;
}
